import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { apiFetch, parseJsonSafe } from '../utils/api';

const pageSizeOptions = [20, 50, 100, 150];

const TaskTimesPage = () => {
  const token = localStorage.getItem('token');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [filters, setFilters] = useState({ from: '', to: '', task_id: '', user_id: '' });
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editModal, setEditModal] = useState({ open: false, item: null, hours: 0, minutes: 0, comments: '', date: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    window.location.href = '/login';
  };

  const fetchTasksList = async () => {
    try {
      const res = await apiFetch('/api/tasks?page=1&pageSize=200', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      const arr = Array.isArray(data) ? data : data.items || [];
      setTasks(arr);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiFetch('/api/users', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.ok && Array.isArray(data)) setUsers(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTimes = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, pageSize });
      if (filters.from) params.append('from', filters.from);
      if (filters.to) params.append('to', filters.to);
      if (filters.task_id) params.append('task_id', filters.task_id);
      if (filters.user_id) params.append('user_id', filters.user_id);
      const res = await apiFetch(`/api/tasks/times?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando tiempos');
      setItems(data.items || []);
      setTotal(Number(data.total || 0));
      setTotalMinutes(Number(data.totalMinutes || 0));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveEdit = async () => {
    const mins = Number(editModal.hours) * 60 + Number(editModal.minutes || 0);
    if (!mins) return alert('Tiempo debe ser mayor que 0');
    setSavingEdit(true);
    try {
      const res = await apiFetch(`/api/tasks/times/${editModal.item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ minutes: mins, comments: editModal.comments, date: editModal.date })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar');
      setEditModal({ open: false, item: null, hours: 0, minutes: 0, comments: '', date: '' });
      fetchTimes();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    fetchTimes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filters]);

  useEffect(() => {
    fetchTasksList();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = useMemo(() => Math.max(Math.ceil(total / pageSize), 1), [total, pageSize]);
  const formatMinutes = (m) => {
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${h}h ${min}m`;
  };
  const formatDateOnly = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString();
  };
  const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString();
  };

  const taskOptions = tasks.map((t) => ({ value: t.id, label: t.titulo || `Tarea ${t.id}` }));
  const userOptions = users.map((u) => ({ value: u.id, label: u.name || u.email }));

  return (
    <div className="small">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Tiempos dedicados</h2>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-3">
              <label className="form-label">Desde</label>
              <input type="date" className="form-control" value={filters.from} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, from: e.target.value })); }} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Hasta</label>
              <input type="date" className="form-control" value={filters.to} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, to: e.target.value })); }} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Tarea</label>
              <Select
                classNamePrefix="select"
                placeholder="Todas las tareas"
                isClearable
                options={taskOptions}
                value={taskOptions.find((o) => String(o.value) === String(filters.task_id)) || null}
                onChange={(opt) => { setPage(1); setFilters((p) => ({ ...p, task_id: opt ? opt.value : '' })); }}
                onInputChange={(val) => {
                  if (val && val.length >= 2) {
                    apiFetch(`/api/tasks?page=1&pageSize=50&search=${encodeURIComponent(val)}`, { headers: { Authorization: `Bearer ${token}` } })
                      .then((res) => parseJsonSafe(res).then((data) => ({ res, data })))
                      .then(({ res, data }) => {
                        if (res.ok && data) {
                          const items = Array.isArray(data) ? data : data.items || [];
                          if (items.length) setTasks((prev) => {
                            const merged = [...prev];
                            items.forEach((t) => { if (!merged.some((m) => m.id === t.id)) merged.push(t); });
                            return merged;
                          });
                        }
                      })
                      .catch(() => {});
                  }
                }}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Usuario</label>
              <Select
                classNamePrefix="select"
                placeholder="Todos los usuarios"
                isClearable
                options={userOptions}
                value={userOptions.find((o) => String(o.value) === String(filters.user_id)) || null}
                onChange={(opt) => { setPage(1); setFilters((p) => ({ ...p, user_id: opt ? opt.value : '' })); }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          {loading ? <p>Cargando...</p> : (
            <>
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Fecha trabajo</th>
                      <th>Registrado el</th>
                      <th>Tarea</th>
                      <th>Usuario</th>
                      <th>Tiempo</th>
                      <th>Comentarios</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 && <tr><td colSpan="7">Sin registros.</td></tr>}
                    {items.map((it) => (
                      <tr key={it.id}>
                        <td>{formatDateOnly(it.date)}</td>
                        <td className="small text-muted">{formatDateTime(it.created_at)}</td>
                        <td>{it.titulo || `Tarea ${it.task_id}`}</td>
                        <td>{it.user_name || it.user_email || '-'}</td>
                        <td>{formatMinutes(it.minutes)}</td>
                        <td>{it.comments || '-'}</td>
                        <td>
                          <button className="btn btn-outline-primary btn-sm" onClick={() => {
                            const h = Math.floor(it.minutes / 60);
                            const m = it.minutes % 60;
                            setEditModal({
                              open: true, item: it,
                              hours: h, minutes: m,
                              comments: it.comments || '',
                              date: it.date ? new Date(it.date).toISOString().slice(0, 10) : ''
                            });
                          }}>Editar</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-between align-items-center mt-2">
                <div>Página {page} de {totalPages} (total {total})</div>
                <div className="d-flex gap-2 align-items-center">
                  <select className="form-select form-select-sm" style={{ width: 110 }} value={pageSize} onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}>
                    {pageSizeOptions.map((n) => <option key={n} value={n}>{n} / pág</option>)}
                  </select>
                  <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
                  <button className="btn btn-outline-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
                </div>
              </div>
              <div className="mt-3 fw-bold">Total tiempo: {formatMinutes(totalMinutes)}</div>
            </>
          )}
        </div>
      </div>

      {editModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Editar tiempo - {editModal.item?.titulo || `Tarea ${editModal.item?.task_id}`}</h5>
                <button className="btn-close" onClick={() => setEditModal({ open: false, item: null, hours: 0, minutes: 0, comments: '', date: '' })}></button>
              </div>
              <div className="modal-body">
                <div className="mb-2">
                  <label className="form-label">Fecha</label>
                  <input type="date" className="form-control" value={editModal.date} onChange={(e) => setEditModal((p) => ({ ...p, date: e.target.value }))} />
                </div>
                <div className="row g-2 mb-2">
                  <div className="col-6">
                    <label className="form-label">Horas</label>
                    <input type="number" min="0" className="form-control" value={editModal.hours} onChange={(e) => setEditModal((p) => ({ ...p, hours: Number(e.target.value) }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Minutos</label>
                    <input type="number" min="0" max="59" className="form-control" value={editModal.minutes} onChange={(e) => setEditModal((p) => ({ ...p, minutes: Number(e.target.value) }))} />
                  </div>
                </div>
                <div className="mb-2">
                  <label className="form-label">Comentarios</label>
                  <textarea className="form-control" rows="3" value={editModal.comments} onChange={(e) => setEditModal((p) => ({ ...p, comments: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setEditModal({ open: false, item: null, hours: 0, minutes: 0, comments: '', date: '' })}>Cerrar</button>
                <button className="btn btn-primary" disabled={savingEdit} onClick={saveEdit}>{savingEdit ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskTimesPage;
